declare global {
    interface Window {
        __INITIAL_ENV__: Record<string, string>;
    }
}

const ENV = process.env;

const throwEnvExistenceError = (env: string) => {
    throw new Error(`env: ${env} is not properly defined`);
};

// NODE_ENV - defines build environment (production or development - may be extended)
export const isProduction: boolean = ENV.NODE_ENV === "production";
export const isDevelopment: boolean = ENV.NODE_ENV === "development";
export const isTest: boolean = ENV.NODE_ENV === "test";

if (!isProduction && !isDevelopment && !isTest) {
    throwEnvExistenceError("NODE_ENV");
}

// EXEC_ENV - defines execution environment (browser or server)
export const isBrowser: boolean = ENV.EXEC_ENV === "browser";
export const isServer: boolean = ENV.EXEC_ENV === "server";

if (!isBrowser && !isServer) {
    throwEnvExistenceError("EXEC_ENV");
}

const UNIVERSAL_ENV = isBrowser ? window.__INITIAL_ENV__ : ENV;

// BUILD_IMAGE_TAG - image tag - provided by dockerfile
export const buildImageTag: string | undefined = UNIVERSAL_ENV.BUILD_IMAGE_TAG;
// BUGSNAG_RELEASE_STAGE — define release stage, where only "production" and "staging" are reported
export const bugsnagReleaseStage = (UNIVERSAL_ENV.BUGSNAG_RELEASE_STAGE ?? "development") as
    | "stage:top"
    | "stage:dev"
    | "stage:ovh"
    | "production"
    | "development";
// RP_REDIRECT_URL - redirect URL to RP app
export const rpRedirectUrl: string | undefined = UNIVERSAL_ENV.RP_REDIRECT_URL;

if (!rpRedirectUrl) {
    throwEnvExistenceError("RP_REDIRECT_URL");
}

// GH_REDIRECT_URL - redirect URL to GH app
export const ghRedirectUrl: string | undefined = UNIVERSAL_ENV.GH_REDIRECT_URL;

if (!ghRedirectUrl) {
    throwEnvExistenceError("GH_REDIRECT_URL");
}

// FACEBOOK_APP_ID - define appId for Facebook auth
export const facebookAppId: string | undefined = UNIVERSAL_ENV.FACEBOOK_APP_ID;
// AMPLITUDE_API_KEY - define Amplitude API key. Scripts should be initialized only if this is defined.
export const amplitudeApiKey: string | undefined = UNIVERSAL_ENV.AMPLITUDE_API_KEY;
// AMPLITUDE_DEPLOYMENT_KEY - client-side Amplitude Experiment deployment key (prefix: client-)
export const amplitudeDeploymentKey: string | undefined = UNIVERSAL_ENV.AMPLITUDE_DEPLOYMENT_KEY;
// AMPLITUDE_ENVIRONMENT - normalized Amplitude environment marker used for analytics filtering
export const amplitudeEnvironment: string | undefined = UNIVERSAL_ENV.AMPLITUDE_ENVIRONMENT;
// AMPLITUDE_EXPERIMENT_SERVER_DEPLOYMENT_KEY - server-side Amplitude Experiment deployment key (prefix: server-)
export const amplitudeExperimentServerDeploymentKey: string | undefined = UNIVERSAL_ENV.AMPLITUDE_EXPERIMENT_SERVER_DEPLOYMENT_KEY;
// USER_COM_ID - define user.com ID. Scripts should be initialized only if this is defined.
export const userComId: string | undefined = UNIVERSAL_ENV.USER_COM_ID;
// USER_COM_APP_SUBDOMAIN - define user.com instance subdomain
export const userComAppSubdomain: string | undefined = UNIVERSAL_ENV.USER_COM_APP_SUBDOMAIN;
// GTM_ID - defines Google Tag Manager ID for production and staging environment
export const gtmId: string | undefined = UNIVERSAL_ENV.GTM_ID;
// GTM_URL - defines GTM URL for production and staging environment
export const gtmUrl: string | undefined = UNIVERSAL_ENV.GTM_URL;
// GTM_LOAD_URL - defines GTM LOAD URL for production and staging environment
export const gtmLoadUrl: string | undefined = UNIVERSAL_ENV.GTM_LOAD_URL;
// GTM_SCRIPT_ID - defines GTM SCRIPT ID for production and staging environment
export const gtmScriptId: string | undefined = UNIVERSAL_ENV.GTM_SCRIPT_ID;
// GTM_SCRIPT_PARAMS - defines GTM SCRIPT PARAMS for production and staging environment
export const gtmScriptParams: string | undefined = UNIVERSAL_ENV.GTM_SCRIPT_PARAMS;
// ALGOLYTICS_ID - define algolytics ID used to report events to big data. When null algolytics won't be used
export const algolyticsId: string | undefined = UNIVERSAL_ENV.ALGOLYTICS_ID;
// SHOW_ALGOLYTICS_LOGS - enable logs for Algolytics events - not for use in production
export const showAlgolyticsLogs: boolean = UNIVERSAL_ENV.SHOW_ALGOLYTICS_LOGS === "true";
//OpenStreetMap
export const osmTileUrl: string | null = UNIVERSAL_ENV.OSM_TILE_URL ?? null;
if (isServer && osmTileUrl === null) {
    throw new Error("env: OSM_TILE_URL is not defined for server environment");
}
// KM_API_URL - define KM API URL for absolute paths in expressjs
export const kmApiUrl: string | undefined = UNIVERSAL_ENV.KM_API_URL;
// KM_COOKIE_DOMAIN - defines cookie domain for KM internal/server-set cookies
export const kmInternalApiCookieDomain: string = (UNIVERSAL_ENV.KM_COOKIE_DOMAIN ?? null) as string;
if (isServer && kmInternalApiCookieDomain == null) {
    throw new Error("env: KM_COOKIE_DOMAIN is not defined for server environment");
}
// BIG_DATA_API_URL - defines BigData API url to fetch statistics data
export const bigDataApiUrl: string | null = UNIVERSAL_ENV.BIG_DATA_API_URL ?? null;
// KM_FEATURE_FLAG_HOME_MATCH - define if home match feature flag is enabled
export const kmFeatureFlagHomeMatch: boolean = String(UNIVERSAL_ENV.KM_FEATURE_FLAG_HOME_MATCH) === "true";
// KM_FEATURE_FLAG_DEVTOOLS - define if pgdevtools feature flag is enabled
export const kmFeatureFlagDevtools: boolean = UNIVERSAL_ENV.KM_FEATURE_FLAG_DEVTOOLS === "true";

export const weglotApiKey: string | null = UNIVERSAL_ENV.WEGLOT_API_KEY ?? null;
// KM_TURNSTILE_SITE_KEY - public Cloudflare Turnstile site key used by the registration form
export const kmTurnstileSiteKey: string | null = UNIVERSAL_ENV.KM_TURNSTILE_SITE_KEY ?? null;

export const BROWSER_ENV = {
    BUILD_IMAGE_TAG: buildImageTag,
    BIG_DATA_API_URL: bigDataApiUrl,
    BUGSNAG_RELEASE_STAGE: bugsnagReleaseStage,
    GTM_ID: gtmId,
    GTM_URL: gtmUrl,
    GTM_LOAD_URL: gtmLoadUrl,
    GTM_SCRIPT_ID: gtmScriptId,
    GTM_SCRIPT_PARAMS: gtmScriptParams,
    GH_REDIRECT_URL: ghRedirectUrl,
    RP_REDIRECT_URL: rpRedirectUrl,
    KM_COOKIE_DOMAIN: kmInternalApiCookieDomain,
    FACEBOOK_APP_ID: facebookAppId,
    AMPLITUDE_API_KEY: amplitudeApiKey,
    AMPLITUDE_DEPLOYMENT_KEY: amplitudeDeploymentKey,
    AMPLITUDE_ENVIRONMENT: amplitudeEnvironment,
    USER_COM_ID: userComId,
    USER_COM_APP_SUBDOMAIN: userComAppSubdomain,
    ALGOLYTICS_ID: algolyticsId,
    SHOW_ALGOLYTICS_LOGS: UNIVERSAL_ENV.SHOW_ALGOLYTICS_LOGS,
    OSM_TILE_URL: osmTileUrl,
    KM_FEATURE_FLAG_HOME_MATCH: kmFeatureFlagHomeMatch,
    KM_FEATURE_FLAG_DEVTOOLS: kmFeatureFlagDevtools,
    WEGLOT_API_KEY: weglotApiKey,
    KM_TURNSTILE_SITE_KEY: kmTurnstileSiteKey
};
declare global {
    interface Window {
        __INITIAL_ENV__: Record<string, string>;
    }
}

const ENV = process.env;

const throwEnvExistenceError = (env: string) => {
    throw new Error(`env: ${env} is not properly defined`);
};

// NODE_ENV - defines build environment (production or development - may be extended)
export const isProduction: boolean = ENV.NODE_ENV === "production";
export const isDevelopment: boolean = ENV.NODE_ENV === "development";
export const isTest: boolean = ENV.NODE_ENV === "test";

if (!isProduction && !isDevelopment && !isTest) {
    throwEnvExistenceError("NODE_ENV");
}

// EXEC_ENV - defines execution environment (browser or server)
export const isBrowser: boolean = ENV.EXEC_ENV === "browser";
export const isServer: boolean = ENV.EXEC_ENV === "server";

if (!isBrowser && !isServer) {
    throwEnvExistenceError("EXEC_ENV");
}

const UNIVERSAL_ENV = isBrowser ? window.__INITIAL_ENV__ : ENV;

// BUILD_IMAGE_TAG - image tag - provided by dockerfile
export const buildImageTag: string | undefined = UNIVERSAL_ENV.BUILD_IMAGE_TAG;
// BUGSNAG_RELEASE_STAGE — define release stage, where only "production" and "staging" are reported
export const bugsnagReleaseStage = (UNIVERSAL_ENV.BUGSNAG_RELEASE_STAGE ?? "development") as
    | "stage:top"
    | "stage:dev"
    | "stage:ovh"
    | "production"
    | "development";
// RP_REDIRECT_URL - redirect URL to RP app
export const rpRedirectUrl: string | undefined = UNIVERSAL_ENV.RP_REDIRECT_URL;

if (!rpRedirectUrl) {
    throwEnvExistenceError("RP_REDIRECT_URL");
}

// GH_REDIRECT_URL - redirect URL to GH app
export const ghRedirectUrl: string | undefined = UNIVERSAL_ENV.GH_REDIRECT_URL;

if (!ghRedirectUrl) {
    throwEnvExistenceError("GH_REDIRECT_URL");
}

// FACEBOOK_APP_ID - define appId for Facebook auth
export const facebookAppId: string | undefined = UNIVERSAL_ENV.FACEBOOK_APP_ID;
// AMPLITUDE_API_KEY - define Amplitude API key. Scripts should be initialized only if this is defined.
export const amplitudeApiKey: string | undefined = UNIVERSAL_ENV.AMPLITUDE_API_KEY;
// AMPLITUDE_DEPLOYMENT_KEY - client-side Amplitude Experiment deployment key (prefix: client-)
export const amplitudeDeploymentKey: string | undefined = UNIVERSAL_ENV.AMPLITUDE_DEPLOYMENT_KEY;
// AMPLITUDE_ENVIRONMENT - normalized Amplitude environment marker used for analytics filtering
export const amplitudeEnvironment: string | undefined = UNIVERSAL_ENV.AMPLITUDE_ENVIRONMENT;
// AMPLITUDE_EXPERIMENT_SERVER_DEPLOYMENT_KEY - server-side Amplitude Experiment deployment key (prefix: server-)
export const amplitudeExperimentServerDeploymentKey: string | undefined = UNIVERSAL_ENV.AMPLITUDE_EXPERIMENT_SERVER_DEPLOYMENT_KEY;
// USER_COM_ID - define user.com ID. Scripts should be initialized only if this is defined.
export const userComId: string | undefined = UNIVERSAL_ENV.USER_COM_ID;
// USER_COM_APP_SUBDOMAIN - define user.com instance subdomain
export const userComAppSubdomain: string | undefined = UNIVERSAL_ENV.USER_COM_APP_SUBDOMAIN;
// GTM_ID - defines Google Tag Manager ID for production and staging environment
export const gtmId: string | undefined = UNIVERSAL_ENV.GTM_ID;
// GTM_URL - defines GTM URL for production and staging environment
export const gtmUrl: string | undefined = UNIVERSAL_ENV.GTM_URL;
// GTM_LOAD_URL - defines GTM LOAD URL for production and staging environment
export const gtmLoadUrl: string | undefined = UNIVERSAL_ENV.GTM_LOAD_URL;
// GTM_SCRIPT_ID - defines GTM SCRIPT ID for production and staging environment
export const gtmScriptId: string | undefined = UNIVERSAL_ENV.GTM_SCRIPT_ID;
// GTM_SCRIPT_PARAMS - defines GTM SCRIPT PARAMS for production and staging environment
export const gtmScriptParams: string | undefined = UNIVERSAL_ENV.GTM_SCRIPT_PARAMS;
// ALGOLYTICS_ID - define algolytics ID used to report events to big data. When null algolytics won't be used
export const algolyticsId: string | undefined = UNIVERSAL_ENV.ALGOLYTICS_ID;
// SHOW_ALGOLYTICS_LOGS - enable logs for Algolytics events - not for use in production
export const showAlgolyticsLogs: boolean = UNIVERSAL_ENV.SHOW_ALGOLYTICS_LOGS === "true";
//OpenStreetMap
export const osmTileUrl: string | null = UNIVERSAL_ENV.OSM_TILE_URL ?? null;
if (isServer && osmTileUrl === null) {
    throw new Error("env: OSM_TILE_URL is not defined for server environment");
}
// KM_API_URL - define KM API URL for absolute paths in expressjs
export const kmApiUrl: string | undefined = UNIVERSAL_ENV.KM_API_URL;
// KM_COOKIE_DOMAIN - defines cookie domain for KM internal/server-set cookies
export const kmInternalApiCookieDomain: string = (UNIVERSAL_ENV.KM_COOKIE_DOMAIN ?? null) as string;
if (isServer && kmInternalApiCookieDomain == null) {
    throw new Error("env: KM_COOKIE_DOMAIN is not defined for server environment");
}
// BIG_DATA_API_URL - defines BigData API url to fetch statistics data
export const bigDataApiUrl: string | null = UNIVERSAL_ENV.BIG_DATA_API_URL ?? null;
// KM_FEATURE_FLAG_HOME_MATCH - define if home match feature flag is enabled
export const kmFeatureFlagHomeMatch: boolean = String(UNIVERSAL_ENV.KM_FEATURE_FLAG_HOME_MATCH) === "true";
// KM_FEATURE_FLAG_DEVTOOLS - define if pgdevtools feature flag is enabled
export const kmFeatureFlagDevtools: boolean = UNIVERSAL_ENV.KM_FEATURE_FLAG_DEVTOOLS === "true";

export const weglotApiKey: string | null = UNIVERSAL_ENV.WEGLOT_API_KEY ?? null;
// KM_TURNSTILE_SITE_KEY - public Cloudflare Turnstile site key used by the registration form
export const kmTurnstileSiteKey: string | null = UNIVERSAL_ENV.KM_TURNSTILE_SITE_KEY ?? null;

export const BROWSER_ENV = {
    BUILD_IMAGE_TAG: buildImageTag,
    BIG_DATA_API_URL: bigDataApiUrl,
    BUGSNAG_RELEASE_STAGE: bugsnagReleaseStage,
    GTM_ID: gtmId,
    GTM_URL: gtmUrl,
    GTM_LOAD_URL: gtmLoadUrl,
    GTM_SCRIPT_ID: gtmScriptId,
    GTM_SCRIPT_PARAMS: gtmScriptParams,
    GH_REDIRECT_URL: ghRedirectUrl,
    RP_REDIRECT_URL: rpRedirectUrl,
    KM_COOKIE_DOMAIN: kmInternalApiCookieDomain,
    FACEBOOK_APP_ID: facebookAppId,
    AMPLITUDE_API_KEY: amplitudeApiKey,
    AMPLITUDE_DEPLOYMENT_KEY: amplitudeDeploymentKey,
    AMPLITUDE_ENVIRONMENT: amplitudeEnvironment,
    USER_COM_ID: userComId,
    USER_COM_APP_SUBDOMAIN: userComAppSubdomain,
    ALGOLYTICS_ID: algolyticsId,
    SHOW_ALGOLYTICS_LOGS: UNIVERSAL_ENV.SHOW_ALGOLYTICS_LOGS,
    OSM_TILE_URL: osmTileUrl,
    KM_FEATURE_FLAG_HOME_MATCH: kmFeatureFlagHomeMatch,
    KM_FEATURE_FLAG_DEVTOOLS: kmFeatureFlagDevtools,
    WEGLOT_API_KEY: weglotApiKey,
    KM_TURNSTILE_SITE_KEY: kmTurnstileSiteKey
};
